import type { Agent, Entity, Persisted } from '@tunasong/schemas'
import { isAgent, roleForParent } from '@tunasong/schemas'
import { entitiesApi, features } from '@tunasong/redux'
import type { TunaThunkDispatch } from '@tunasong/redux'
import { Brain, getIconByKey } from '@tunasong/icons'
import { blockAffordanceToCommand } from './blockaffordance-to-command.js'
import type { AgentDefinition, Command } from './plugin-types.js'
import { sentenceCase } from './sentence-case.js'

interface AgentCommandContext {
  dispatch: TunaThunkDispatch
  getState: () => unknown
  /**
   * The parent's agent children from a live subscription (GH #2176). Without it the command list is
   * built from a getState() snapshot that nothing re-reads, so Add stays on offer after the agent exists.
   */
  agents?: Persisted<Agent>[]
}

const selectCachedAgents = (getState: () => unknown, parentId: string) => {
  const cached = entitiesApi.endpoints.loadChildEntities.select({ parentId })(getState() as never)
  return (cached?.data ?? []).filter(isAgent) as Persisted<Agent>[]
}

/**
 * The agent with this definition on the parent, read when Add runs: the cache first, then the server,
 * so a stale menu can't create a second one (GH #2176).
 */
const findExistingAgent = async (
  context: AgentCommandContext,
  parentId: string,
  definitionId: string
): Promise<Persisted<Agent> | undefined> => {
  const cached = selectCachedAgents(context.getState, parentId).find(a => a.definitionId === definitionId)
  if (cached) {
    return cached
  }
  const { data } = (await context.dispatch(
    entitiesApi.endpoints.loadChildEntities.initiate({ parentId }, { forceRefetch: true, subscribe: false }) as never
  )) as { data?: unknown }
  const children = Array.isArray(data) ? (data as Persisted<Entity>[]) : []
  return (children.filter(isAgent) as Persisted<Agent>[]).find(a => a.definitionId === definitionId)
}

/**
 * Every "Add agent" command, across definitions, shares this group, so the entity menu collapses them into one
 * "Add agent ›" submenu with a row per agent.
 */
const addAgentGroupId = 'add-agent'

/**
 * Convert an AgentDefinition into commands for the entity command menu.
 * - When no agent with this definitionId exists: "Add agent: X" (creates it and opens it in the sidebar)
 * - When one already exists: "Open agent: X" (opens it in the sidebar)
 */
export const agentDefinitionToCommands = (
  definition: AgentDefinition,
  entity: Persisted<Entity>,
  context: AgentCommandContext
): Command[] => {
  const { dispatch, getState } = context

  // Group tagging shared by every command this definition produces (the open/add
  // command plus all affordance commands). The entity context menu uses these to
  // collapse one agent's commands into a single flyout submenu. `name` is left
  // fully-qualified for the flat palette / command search; `shortName` carries the
  // prefix-free label the submenu renders.
  const groupIcon = getIconByKey(definition.iconKey)
  const displayName = sentenceCase(definition.name)
  const groupTags = {
    groupId: definition.id,
    groupLabel: displayName,
    groupIcon,
  }

  const existing = context.agents ?? selectCachedAgents(getState, entity.id)
  const existingAgent = existing.find(a => a.definitionId === definition.id)

  if (existingAgent) {
    const openCommand: Command = {
      id: `agent-open-${definition.id}`,
      name: `Open agent: ${displayName}`,
      shortName: 'Open agent',
      ...groupTags,
      Icon: groupIcon,
      blockOnRun: false,
      // Stay on the parent, like the Agents pill: the agent's own page replaces the song (GH #2169)
      cmd: () => {
        dispatch(features.ui.actions.openAgentComposer({ agentId: existingAgent.id }))
      },
    }
    const affordanceCommands =
      definition.blockAffordances?.map(aff => blockAffordanceToCommand(definition, existingAgent, aff)) ?? []
    return [openCommand, ...affordanceCommands]
  }

  return [
    {
      id: `agent-add-${definition.id}`,
      name: `Add agent: ${displayName}`,
      // One "Add agent ›" submenu for every definition, each row named for its agent
      shortName: displayName,
      groupId: addAgentGroupId,
      groupLabel: 'Add agent',
      groupIcon: Brain,
      Icon: groupIcon,
      blockOnRun: true,
      cmd: async () => {
        // The menu may predate an agent created since: open that one instead of adding a duplicate.
        const alreadyAdded = await findExistingAgent(context, entity.id, definition.id)
        if (alreadyAdded) {
          dispatch(features.ui.actions.openAgentComposer({ agentId: alreadyAdded.id }))
          return
        }

        const config: Agent['config'] = {
          proactivity: (definition.defaultConfig?.proactivity ?? 'normal') as Agent['config']['proactivity'],
          triggers: definition.defaultConfig?.triggers ?? { onChange: true },
          // allowedTools intentionally NOT snapshotted — the server resolves it
          // from the live definition at request time. See spec §5.
        }

        const agentEntity: Partial<Agent> = {
          type: 'agent',
          parentId: entity.id,
          name: definition.name,
          definitionId: definition.id,
          // roleLine intentionally NOT snapshotted, like allowedTools: the server reads it from the live
          // definition (run-agent), and a copy went stale on every definition update.
          role: roleForParent(entity.type),
          status: 'active',
          config,
          memory: {},
        }

        const result = await dispatch(
          entitiesApi.endpoints.createEntity.initiate({
            entity: agentEntity as Agent,
            parent: entity,
          }) as never
        )

        // Stay on the parent and surface the new agent in the sidebar, like the Agents pill.
        // Navigating to the agent's own page replaced the song's header, More menu and
        // editor with the agent's, and a reload stayed there (GH #2169).
        const created = (result as { data?: Persisted<Entity> }).data
        if (created) {
          dispatch(features.ui.actions.openAgentComposer({ agentId: created.id }))
        }
      },
    },
  ]
}
